/**
 * PriceListTab — Item prices rendered with one cell per parent column.
 * Uses shared SubTableRow/SubTableHeadRow components and centralized styling.
 * Includes inline "Add price" action in footer zone.
 */

import { useState, useRef } from "react";
import { Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ItemPriceEntry } from "../../api/pricingSummaryHooks";
import { useTableStyles, SUB_TABLE_DATA_ROW_CLASS, SUB_TABLE_SKELETON_CLASS, SUB_TABLE_ACTION_BUTTON_CLASS } from "./useTableStyles";
import { SubTableRow, SubTableHeadRow } from "./SubTableRow";
import { AddPriceRow } from "./AddPriceRow";

const fmt = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

interface PriceListTabProps {
  data: ItemPriceEntry[] | undefined;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
  itemCode: string;
  columnWidths: number[];
}

/** Map child fields to parent column indices. Skip first 3 system cols. */
function getFieldPositions(colCount: number) {
  const start = Math.min(3, colCount - 1);
  return {
    priceList: start,
    rate: Math.min(start + 1, colCount - 1),
    currency: Math.min(start + 2, colCount - 1),
    uom: Math.min(start + 3, colCount - 1),
    validFrom: Math.min(start + 4, colCount - 1),
    validTo: Math.min(start + 5, colCount - 1),
  };
}

export function PriceListTab({ data, isLoading, isError, refetch, itemCode, columnWidths }: PriceListTabProps) {
  const [showAddRow, setShowAddRow] = useState(false);
  const addBtnRef = useRef<HTMLButtonElement>(null);
  const { headCellClass, dataCellClass, headMinHeight, dataMinHeight } = useTableStyles();
  const pos = getFieldPositions(columnWidths.length);

  const headerCells: Record<number, { label: string; align?: "right" }> = {
    [pos.priceList]: { label: "Price List" },
    [pos.rate]: { label: "Rate", align: "right" },
    [pos.currency]: { label: "Currency" },
    [pos.uom]: { label: "UOM" },
    [pos.validFrom]: { label: "Valid From" },
    [pos.validTo]: { label: "Valid To" },
  };

  const handleSaved = () => {
    setShowAddRow(false);
    setTimeout(() => addBtnRef.current?.focus(), 50);
  };

  const handleCancel = () => {
    setShowAddRow(false);
    setTimeout(() => addBtnRef.current?.focus(), 50);
  };

  const addPriceButton = (
    <div className={cn(dataCellClass, "text-muted-foreground")} style={{ minHeight: dataMinHeight }}>
      <button
        ref={addBtnRef}
        onClick={() => setShowAddRow(true)}
        aria-expanded={showAddRow}
        className={SUB_TABLE_ACTION_BUTTON_CLASS}
      >
        <Plus className="h-3 w-3" /> Add price
      </button>
    </div>
  );

  const addRowElement = (
    <AddPriceRow
      itemCode={itemCode}
      columnWidths={columnWidths}
      positions={{ priceList: pos.priceList, rate: pos.rate, currency: pos.currency }}
      existingPrices={data ?? []}
      onSaved={handleSaved}
      onCancel={handleCancel}
    />
  );

  if (isLoading) {
    const skeleton = <div className={cn("h-3.5 w-16", SUB_TABLE_SKELETON_CLASS)} />;
    return (
      <div>
        <SubTableHeadRow columnWidths={columnWidths} cells={headerCells} headClass={headCellClass} minHeight={headMinHeight} />
        {[1, 2].map((i) => (
          <SubTableRow
            key={i}
            columnWidths={columnWidths}
            cellClass={dataCellClass}
            minHeight={dataMinHeight}
            className={SUB_TABLE_DATA_ROW_CLASS}
            cells={{
              [pos.priceList]: <div className={cn("h-3.5 w-24", SUB_TABLE_SKELETON_CLASS)} />,
              [pos.rate]: skeleton,
              [pos.currency]: skeleton,
              [pos.uom]: skeleton,
              [pos.validFrom]: skeleton,
              [pos.validTo]: skeleton,
            }}
          />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className={cn(dataCellClass, "text-destructive")} style={{ minHeight: dataMinHeight }}>
        Could not load prices ·{" "}
        <button onClick={refetch} className="underline hover:no-underline">Retry</button>
      </div>
    );
  }

  // Empty state — show inline add trigger
  if (!data || data.length === 0) {
    return (
      <div>
        {showAddRow ? (
          <>
            <SubTableHeadRow columnWidths={columnWidths} cells={headerCells} headClass={headCellClass} minHeight={headMinHeight} />
            {addRowElement}
          </>
        ) : (
          <div className={cn(dataCellClass, "text-muted-foreground")} style={{ minHeight: dataMinHeight }}>
            No prices configured ·{" "}
            <button
              ref={addBtnRef}
              onClick={() => setShowAddRow(true)}
              className="underline hover:no-underline"
            >
              Add price
            </button>
          </div>
        )}
      </div>
    );
  }

  // Data rows + footer action
  return (
    <div>
      <SubTableHeadRow columnWidths={columnWidths} cells={headerCells} headClass={headCellClass} minHeight={headMinHeight} />
      {data.map((entry) => (
        <SubTableRow
          key={entry.name}
          columnWidths={columnWidths}
          cellClass={dataCellClass}
          minHeight={dataMinHeight}
          cellExtraClass={{ [pos.rate]: "text-right" }}
          className={SUB_TABLE_DATA_ROW_CLASS}
          cells={{
            [pos.priceList]: <span className="truncate">{entry.price_list}</span>,
            [pos.rate]: <span className="tabular-nums">{fmt.format(entry.price_list_rate)}</span>,
            [pos.currency]: <span className="truncate">{entry.currency}</span>,
            [pos.uom]: <span className="truncate">{entry.uom}</span>,
            [pos.validFrom]: entry.valid_from ?? "—",
            [pos.validTo]: entry.valid_upto ?? "—",
          }}
        />
      ))}
      {showAddRow ? addRowElement : addPriceButton}
    </div>
  );
}
